import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Redirect } from 'react-router-dom';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';

import { AlbumFolderWithCreatorBrandDetails } from '../../../graphql/queries/album-folder';
import {
  albumFolderSubscriptionRequired,
  albumFolderSupportsSimplifiedJoin,
} from '../../../util/album-folder';
import Spinner from '../../global/Spinner';
import { ALBUM_FOLDER_JOIN_TYPE } from '../../../constants/album-folder';

/**
 * Higher order component for the album folder allows purchase only functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {React.Component} The composed component.
 */
const WithAlbumFolderAllowsPurchaseOnly = WrappedComponent =>
  class WithAlbumFolderAllowsPurchaseOnlyClass extends Component {
    static propTypes = {
      rootAlbumFolder: PropTypes.shape({
        id: PropTypes.string.isRequired,
        join_type: PropTypes.oneOf(Object.values(ALBUM_FOLDER_JOIN_TYPE)),
      }),
      loading: PropTypes.bool,
    };

    render() {
      const { rootAlbumFolder, loading } = this.props;

      if (loading || !rootAlbumFolder) {
        return (
          <div className="wrapper-main min-h-100vh justify-content-center align-items-center">
            <Spinner />
          </div>
        );
      }

      if (albumFolderSupportsSimplifiedJoin(rootAlbumFolder)) {
        return <Redirect to={`/join/album-folder/${rootAlbumFolder.id}`} />;
      }

      // Will force that discovery is never used
      // For albumFolders that are subscription required
      // i.e. their join_type is ALL or they are .required_album_folder_onboarding == TRUE
      return albumFolderSubscriptionRequired(rootAlbumFolder) ? (
        <Redirect
          to={`/purchase/album-folder/${rootAlbumFolder.id}/purchase-subscription`}
        />
      ) : (
        <WrappedComponent {...this.props} />
      );
    }
  };

/**
 * Higher order container for the allows purchase only functionality.
 *
 * @param {React.Component} WrappedComponent The component to be composed.
 * @returns {Function} The HOC container.
 */
const AlbumFolderAllowsPurchaseOnly = WrappedComponent => {
  const WithPurchasedAlbumFolder = WithAlbumFolderAllowsPurchaseOnly(
    WrappedComponent,
  );

  const withAlbumFolderData = graphql(AlbumFolderWithCreatorBrandDetails, {
    options: ({ match }) => ({
      variables: {
        id: match.params.albumFolderId,
      },
      notifyOnNetworkStatusChange: true,
    }),
    props: ({ data: { albumFolder, loading } }) => ({
      rootAlbumFolder: albumFolder?.root,
      loading,
    }),
  });

  return compose(withAlbumFolderData)(WithPurchasedAlbumFolder);
};

export { WithAlbumFolderAllowsPurchaseOnly };
export default AlbumFolderAllowsPurchaseOnly;
